import React, {
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import map from 'lodash/map';
import SortAscendingIcon from 'core/icons/sortAscending';
import SortDescendingIcon from 'core/icons/sortDescending';
import Button from 'pages/Dashboard/components/Button';
import {
  SortOrder,
} from 'dtos/sortOrder';
import {
  ToDoSortKey,
} from 'dtos/toDoSortKey';
import {
  ToDoSortValue,
} from 'pages/Dashboard/pages/Todos/types';

type SortOption = {
  label: string;
  value: ToDoSortKey;
};

type DirectionOption = {
  label: string;
  value: SortOrder;
};

const sortByOptions: SortOption[] = [
  {
    label: 'Default',
    value: ToDoSortKey.Default,
  },
  {
    label: 'Due date',
    value: ToDoSortKey.DueDate,
  },
  {
    label: 'Urgent',
    value: ToDoSortKey.Urgent,
  },
];

const directionOptions: DirectionOption[] = [
  {
    label: 'Latest to earliest',
    value: SortOrder.Descending,
  },
  {
    label: 'Earliest to latest',
    value: SortOrder.Ascending,
  },
];

type Props = {
  value: ToDoSortValue;
  onChange: (value: ToDoSortValue) => void;
};

export default function ToDoSortMenu({
  value,
  onChange,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const showSortDirection = value.sortBy === ToDoSortKey.DueDate;

  const Icon = useMemo(
    () => (value.sortOrder === SortOrder.Ascending ? SortAscendingIcon : SortDescendingIcon),
    [value.sortOrder],
  );

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSortByChange = useCallback((sortBy: ToDoSortKey) => {
    onChange({
      sortBy,
      sortOrder: value.sortOrder,
    });
    handleClose();
  }, [handleClose, onChange, value.sortOrder]);

  const handleSortOrderChange = useCallback((sortOrder: SortOrder) => {
    onChange({
      sortBy: value.sortBy,
      sortOrder,
    });
    handleClose();
  }, [handleClose, onChange, value.sortBy]);

  return (
    <>
      <Button
        className="nw-todo-sort-button !border-gray-200 !px-4 !py-2 !text-gray-800"
        variant="outlined"
        onClick={handleOpen}
      >
        <Icon className="!w-4 !h-4" />
        <span className="hidden lg:block">Sort</span>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 220,
              mt: 0.5,
            },
          },
          list: {
            autoFocusItem: open,
            sx: {
              py: 0,
              '& .MuiMenuItem-root': {
                py: 1,
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent !important',
                  '&.Mui-focusVisible': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1) !important',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                  },
                },
              },
            },
          },
        }}
      >
        <ListSubheader
          className="!px-3 !py-1.5 mb-0.5 !text-sm !text-gray-700 !font-medium !bg-gray-50 !leading-normal !h-8"
          disableSticky
        >
          Sort by
        </ListSubheader>
        {map(sortByOptions, (option) => {
          const isSelected = value.sortBy === option.value;

          return (
            <MenuItem
              key={option.value}
              onClick={() => { handleSortByChange(option.value); }}
              selected={isSelected}
              className="!text-xs"
            >
              <span className="flex items-center w-full gap-2">
                <span className="w-4 flex items-center justify-center">
                  {isSelected && <CheckIcon className="!w-4 !h-4 text-gray-700" />}
                </span>
                <span className="text-xs font-medium">{option.label}</span>
              </span>
            </MenuItem>
          );
        })}
        {showSortDirection && (
          <>
            <ListSubheader
              className="!px-3 !py-1.5 !text-sm !text-gray-700 !font-medium !bg-gray-50 !border-t !leading-normal !h-8"
              disableSticky
            >
              Sort direction
            </ListSubheader>
            {map(directionOptions, (option) => {
              const isSelected = value.sortOrder === option.value;

              return (
                <MenuItem
                  key={option.value}
                  onClick={() => { handleSortOrderChange(option.value); }}
                  selected={isSelected}
                  className="!text-xs"
                >
                  <span className="flex items-center w-full gap-2">
                    <span className="w-4 flex items-center justify-center">
                      {isSelected && <CheckIcon className="!w-4 !h-4 text-gray-700" />}
                    </span>
                    <span className="text-xs font-medium">{option.label}</span>
                  </span>
                </MenuItem>
              );
            })}
          </>
        )}
      </Menu>
    </>
  );
}
